<template>
  <div class="totp-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">
    <div class="totp-page">
      <div class="page-header">
        <h1 class="page-title">Two-Factor Authentication</h1>
        <p class="page-subtitle">Protect your account with an authenticator app</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="status-card">
        <div class="spinner-wrap"><span class="spinner"></span> Loading...</div>
      </div>

      <!-- Enabled state -->
      <div v-else-if="totpEnabled" class="status-card enabled-card">
        <div class="status-icon enabled-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
        </div>
        <div class="status-info">
          <h2 class="status-title">Two-factor authentication is ON</h2>
          <p class="status-desc">
            Every login requires a code from your authenticator app.
            <span v-if="recoveryCodesRemaining > 0">
              You have <strong>{{ recoveryCodesRemaining }}</strong> recovery code{{ recoveryCodesRemaining !== 1 ? 's' : '' }} remaining.
            </span>
            <span v-else class="no-codes-warning">You have no recovery codes left — consider moving to a new device to generate fresh ones.</span>
          </p>
        </div>

        <!-- Action buttons (idle state) -->
        <div v-if="!showDisable && !showMove" class="action-row action-row-split">
          <button class="btn-secondary" @click="startMove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
              <line x1="12" y1="18" x2="12.01" y2="18"/>
            </svg>
            Move to new device
          </button>
          <button class="btn-danger" @click="showDisable = true">Disable Two-Factor Auth</button>
        </div>

        <!-- Disable form -->
        <div v-if="showDisable" class="disable-form">
          <h3 class="section-label">Confirm to disable</h3>
          <div class="form-group">
            <label class="form-label">Current password</label>
            <input v-model="disableForm.password" type="password" class="form-input" placeholder="Enter your password" />
          </div>
          <div class="form-group">
            <label class="form-label">Authenticator code</label>
            <input v-model="disableForm.code" type="text" inputmode="numeric" maxlength="6" class="form-input totp-input" placeholder="000000" />
          </div>
          <div v-if="disableError" class="error-msg">{{ disableError }}</div>
          <div class="btn-row">
            <button class="btn-secondary" @click="showDisable = false">Cancel</button>
            <button class="btn-danger" :disabled="disabling" @click="handleDisable">
              <span v-if="!disabling">Disable</span>
              <span v-else><span class="spinner sm"></span> Disabling...</span>
            </button>
          </div>
        </div>

        <!-- Move to new device flow -->
        <div v-if="showMove" class="move-flow">

          <!-- Move step 1: verify identity -->
          <div v-if="moveStep === 1">
            <h3 class="section-label">Step 1 — Verify your identity</h3>
            <p class="step-desc">Enter your password and either your current authenticator code or a recovery code.</p>
            <div class="form-group">
              <label class="form-label">Password</label>
              <input v-model="moveForm.password" type="password" class="form-input" placeholder="Enter your password" />
            </div>
            <div class="move-verify-toggle">
              <button
                :class="['toggle-btn', moveVerifyType === 'code' ? 'active' : '']"
                @click="moveVerifyType = 'code'; moveForm.recovery_code = ''"
              >Current code</button>
              <button
                :class="['toggle-btn', moveVerifyType === 'recovery' ? 'active' : '']"
                @click="moveVerifyType = 'recovery'; moveForm.code = ''"
              >Recovery code</button>
            </div>
            <div v-if="moveVerifyType === 'code'" class="form-group">
              <label class="form-label">Current authenticator code</label>
              <input v-model="moveForm.code" type="text" inputmode="numeric" maxlength="6" class="form-input totp-input" placeholder="000000" />
            </div>
            <div v-else class="form-group">
              <label class="form-label">Recovery code</label>
              <input v-model="moveForm.recovery_code" type="text" class="form-input" placeholder="XXXXX-XXXXX" style="letter-spacing:2px;font-family:monospace" />
            </div>
            <div v-if="moveError" class="error-msg">{{ moveError }}</div>
            <div class="btn-row">
              <button class="btn-secondary" @click="cancelMove">Cancel</button>
              <button class="btn-primary" :disabled="movingInit" @click="handleMoveInit">
                <span v-if="!movingInit">Continue &rarr;</span>
                <span v-else><span class="spinner sm"></span> Verifying...</span>
              </button>
            </div>
          </div>

          <!-- Move step 2: scan QR on new phone -->
          <div v-if="moveStep === 2">
            <h3 class="section-label">Step 2 — Set up your new device</h3>
            <p class="step-desc">Open your authenticator app on the <strong>new phone</strong>, tap <strong>+</strong> → <strong>Other account</strong>, then scan this QR code.</p>
            <div class="qr-wrap">
              <img v-if="moveQrDataUrl" :src="moveQrDataUrl" alt="New TOTP QR Code" class="qr-img" />
              <div v-else class="qr-placeholder"><span class="spinner"></span></div>
            </div>
            <details class="manual-entry">
              <summary>Can't scan? Enter the key manually</summary>
              <div class="manual-key">
                <span class="key-label">Account:</span> <span class="key-val">{{ moveQrLabel }}</span><br/>
                <span class="key-label">Key:</span> <span class="key-val secret-key">{{ moveNewSecret }}</span>
              </div>
            </details>
            <p class="move-notice">Your current authenticator is still active until you confirm the new one.</p>
            <div class="btn-row">
              <button class="btn-secondary" @click="moveStep = 1">&larr; Back</button>
              <button class="btn-primary" @click="moveStep = 3">I've scanned it &rarr;</button>
            </div>
          </div>

          <!-- Move step 3: confirm new code -->
          <div v-if="moveStep === 3">
            <h3 class="section-label">Step 3 — Confirm the new device works</h3>
            <p class="step-desc">Enter the 6-digit code shown in your authenticator app on the <strong>new phone</strong>.</p>
            <div class="form-group">
              <input
                v-model="moveConfirmCode"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="form-input totp-input"
                placeholder="000000"
                @keyup.enter="handleMoveConfirm"
              />
            </div>
            <div v-if="moveError" class="error-msg">{{ moveError }}</div>
            <div class="btn-row">
              <button class="btn-secondary" @click="moveStep = 2">&larr; Back</button>
              <button class="btn-primary" :disabled="movingConfirm" @click="handleMoveConfirm">
                <span v-if="!movingConfirm">Activate new device</span>
                <span v-else><span class="spinner sm"></span> Activating...</span>
              </button>
            </div>
          </div>

          <!-- Move step 4: done + new recovery codes -->
          <div v-if="moveStep === 4" class="recovery-card" style="padding:0;box-shadow:none;border:none">
            <div class="status-icon enabled-icon" style="margin-bottom:12px">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <h3 class="section-label" style="color:#059669">New device activated!</h3>
            <p class="step-desc"><strong>Save your new recovery codes.</strong> Your old codes no longer work. Each code can only be used once.</p>
            <div class="recovery-codes-grid">
              <div v-for="code in moveRecoveryCodes" :key="code" class="recovery-code">{{ code }}</div>
            </div>
            <div class="recovery-actions">
              <button class="btn-secondary" @click="copyMoveCodes">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                {{ moveCopied ? 'Copied!' : 'Copy codes' }}
              </button>
            </div>
            <p class="recovery-warning">These codes will not be shown again.</p>
            <button class="btn-primary" @click="finishMove">Done</button>
          </div>

        </div>
      </div>

      <!-- Disabled state: show setup steps -->
      <div v-else class="setup-flow">

        <!-- Step 1: Start setup -->
        <div v-if="setupStep === 0" class="status-card">
          <div class="status-icon disabled-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div class="status-info">
            <h2 class="status-title">Two-factor authentication is OFF</h2>
            <p class="status-desc">Add an extra layer of security. After enabling, every login will require a 6-digit code from your authenticator app.</p>
          </div>
          <div class="action-row">
            <button class="btn-primary" :disabled="startLoading" @click="startSetup">
              <span v-if="!startLoading">Setup Authenticator</span>
              <span v-else><span class="spinner sm"></span> Loading...</span>
            </button>
          </div>
          <div v-if="setupError" class="error-msg">{{ setupError }}</div>
        </div>

        <!-- Step 2: Scan QR code -->
        <div v-if="setupStep === 1" class="status-card">
          <h2 class="step-title">Step 1 — Scan the QR code</h2>
          <p class="step-desc">Open <strong>Microsoft Authenticator</strong> (or any TOTP app), tap <strong>+</strong> → <strong>Other account</strong>, then scan this QR code.</p>

          <div class="qr-wrap">
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="TOTP QR Code" class="qr-img" />
            <div v-else class="qr-placeholder"><span class="spinner"></span></div>
          </div>

          <details class="manual-entry">
            <summary>Can't scan? Enter the key manually</summary>
            <div class="manual-key">
              <span class="key-label">Account:</span> <span class="key-val">{{ qrLabel }}</span><br/>
              <span class="key-label">Key:</span> <span class="key-val secret-key">{{ qrSecret }}</span>
            </div>
          </details>

          <button class="btn-primary" @click="setupStep = 2">I've scanned it &rarr;</button>
        </div>

        <!-- Step 3: Confirm code -->
        <div v-if="setupStep === 2" class="status-card">
          <h2 class="step-title">Step 2 — Confirm it works</h2>
          <p class="step-desc">Enter the 6-digit code currently shown in your authenticator app.</p>

          <div class="form-group">
            <input
              v-model="confirmCode"
              type="text"
              inputmode="numeric"
              maxlength="6"
              class="form-input totp-input"
              placeholder="000000"
              @keyup.enter="handleEnable"
            />
          </div>
          <div v-if="enableError" class="error-msg">{{ enableError }}</div>

          <div class="btn-row">
            <button class="btn-secondary" @click="setupStep = 1">&larr; Back</button>
            <button class="btn-primary" :disabled="enabling" @click="handleEnable">
              <span v-if="!enabling">Enable</span>
              <span v-else><span class="spinner sm"></span> Enabling...</span>
            </button>
          </div>
        </div>

        <!-- Step 4: Recovery codes -->
        <div v-if="setupStep === 3" class="status-card recovery-card">
          <div class="status-icon enabled-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <polyline points="9 12 11 14 15 10"/>
            </svg>
          </div>
          <h2 class="step-title success-title">Two-factor authentication enabled!</h2>
          <p class="step-desc"><strong>Save your recovery codes.</strong> If you ever lose access to your authenticator app, you can use one of these codes to log in. Each code works only once.</p>

          <div class="recovery-codes-grid">
            <div v-for="code in recoveryCodes" :key="code" class="recovery-code">{{ code }}</div>
          </div>

          <div class="recovery-actions">
            <button class="btn-secondary" @click="copyRecoveryCodes">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {{ copied ? 'Copied!' : 'Copy codes' }}
            </button>
          </div>

          <p class="recovery-warning">These codes will not be shown again. Store them somewhere safe.</p>
          <button class="btn-primary" @click="finishSetup">Done</button>
        </div>
      </div>
    </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { AuthService } from '@/services/auth'
import QRCode from 'qrcode'

const API = 'https://api.quali-d.com'

export default {
  name: 'TotpSettingsView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      loading: true,
      totpEnabled: false,
      recoveryCodesRemaining: 0,
      // Setup flow
      setupStep: 0,
      startLoading: false,
      setupError: '',
      qrSecret: '',
      qrLabel: '',
      qrDataUrl: '',
      confirmCode: '',
      enableError: '',
      enabling: false,
      recoveryCodes: [],
      copied: false,
      // Disable flow
      showDisable: false,
      disableForm: { password: '', code: '' },
      disableError: '',
      disabling: false,
      // Move to new device flow
      showMove: false,
      moveStep: 1,
      moveVerifyType: 'code',
      moveForm: { password: '', code: '', recovery_code: '' },
      moveError: '',
      movingInit: false,
      movingConfirm: false,
      moveNewSecret: '',
      moveQrDataUrl: '',
      moveQrLabel: '',
      moveConfirmCode: '',
      moveRecoveryCodes: [],
      moveCopied: false
    }
  },
  async mounted () {
    await this.loadStatus()
  },
  methods: {
    async loadStatus () {
      this.loading = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/status`)
        const data = await res.json()
        if (data.success) {
          this.totpEnabled = data.totp_enabled
          this.recoveryCodesRemaining = data.recovery_codes_remaining
        }
      } catch (e) {
        console.error('Failed to load TOTP status', e)
      } finally {
        this.loading = false
      }
    },

    async startSetup () {
      this.startLoading = true
      this.setupError = ''
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/setup`)
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.qrSecret = data.secret
        this.qrLabel = data.label
        this.qrDataUrl = await QRCode.toDataURL(data.qr_uri, { width: 220, margin: 2, color: { dark: '#1f2937', light: '#ffffff' } })
        this.setupStep = 1
      } catch (e) {
        this.setupError = e.message || 'Failed to start setup'
      } finally {
        this.startLoading = false
      }
    },

    async handleEnable () {
      this.enableError = ''
      if (!this.confirmCode) return
      this.enabling = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/enable`, {
          method: 'POST',
          body: JSON.stringify({ secret: this.qrSecret, code: this.confirmCode })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.recoveryCodes = data.recovery_codes
        this.setupStep = 3
      } catch (e) {
        this.enableError = e.message || 'Failed to enable TOTP'
      } finally {
        this.enabling = false
      }
    },

    async handleDisable () {
      this.disableError = ''
      this.disabling = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/disable`, {
          method: 'POST',
          body: JSON.stringify(this.disableForm)
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.totpEnabled = false
        this.showDisable = false
        this.disableForm = { password: '', code: '' }
        this.setupStep = 0
      } catch (e) {
        this.disableError = e.message || 'Failed to disable TOTP'
      } finally {
        this.disabling = false
      }
    },

    startMove () {
      this.showMove = true
      this.moveStep = 1
      this.moveError = ''
      this.moveForm = { password: '', code: '', recovery_code: '' }
      this.moveVerifyType = 'code'
      this.moveConfirmCode = ''
      this.moveNewSecret = ''
      this.moveQrDataUrl = ''
    },

    cancelMove () {
      this.showMove = false
      this.moveStep = 1
      this.moveError = ''
    },

    async handleMoveInit () {
      this.moveError = ''
      if (!this.moveForm.password) { this.moveError = 'Password is required'; return }
      if (this.moveVerifyType === 'code' && !this.moveForm.code) { this.moveError = 'Enter the authenticator code'; return }
      if (this.moveVerifyType === 'recovery' && !this.moveForm.recovery_code) { this.moveError = 'Enter a recovery code'; return }
      this.movingInit = true
      try {
        const body = { password: this.moveForm.password }
        if (this.moveVerifyType === 'code') body.code = this.moveForm.code
        else body.recovery_code = this.moveForm.recovery_code.trim().toUpperCase()
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/move-init`, {
          method: 'POST',
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.moveNewSecret = data.new_secret
        this.moveQrLabel = data.label
        this.moveQrDataUrl = await QRCode.toDataURL(data.qr_uri, { width: 220, margin: 2, color: { dark: '#1f2937', light: '#ffffff' } })
        this.moveStep = 2
      } catch (e) {
        this.moveError = e.message || 'Verification failed'
      } finally {
        this.movingInit = false
      }
    },

    async handleMoveConfirm () {
      this.moveError = ''
      if (!this.moveConfirmCode) return
      this.movingConfirm = true
      try {
        const res = await AuthService.makeAuthenticatedRequest(`${API}/totp/move-confirm`, {
          method: 'POST',
          body: JSON.stringify({
            password: this.moveForm.password,
            secret: this.moveNewSecret,
            code: this.moveConfirmCode
          })
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.message)
        this.moveRecoveryCodes = data.recovery_codes
        this.recoveryCodesRemaining = data.recovery_codes.length
        this.moveStep = 4
      } catch (e) {
        this.moveError = e.message || 'Failed to activate new device'
      } finally {
        this.movingConfirm = false
      }
    },

    copyMoveCodes () {
      navigator.clipboard.writeText(this.moveRecoveryCodes.join('\n'))
      this.moveCopied = true
      setTimeout(() => { this.moveCopied = false }, 2000)
    },

    finishMove () {
      this.showMove = false
      this.moveStep = 1
      this.moveNewSecret = ''
      this.moveQrDataUrl = ''
      this.moveRecoveryCodes = []
    },

    copyRecoveryCodes () {
      navigator.clipboard.writeText(this.recoveryCodes.join('\n'))
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },

    finishSetup () {
      this.totpEnabled = true
      this.recoveryCodesRemaining = this.recoveryCodes.length
      this.setupStep = 0
      this.recoveryCodes = []
    }
  }
}
</script>

<style scoped>
.totp-layout {
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.totp-page {
  max-width: 620px;
  margin: 0 auto;
  padding: 32px 24px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px;
}

.page-subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 15px;
}

.status-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.08);
  margin-bottom: 24px;
}

.enabled-card {
  border-left: 4px solid #10b981;
}

.status-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.enabled-icon {
  background: #d1fae5;
  color: #059669;
}

.disabled-icon {
  background: #f3f4f6;
  color: #9ca3af;
}

.status-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
}

.status-desc {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  line-height: 1.6;
}

.no-codes-warning {
  color: #ef4444;
}

.action-row {
  margin-top: 24px;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(59,130,246,0.3);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-danger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px;
}

.success-title {
  color: #059669;
}

.step-desc {
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 24px;
  line-height: 1.6;
}

.qr-wrap {
  display: flex;
  justify-content: center;
  margin: 24px 0;
}

.qr-img {
  border: 3px solid #e5e7eb;
  border-radius: 12px;
  padding: 8px;
}

.qr-placeholder {
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border-radius: 12px;
}

.manual-entry {
  margin-bottom: 24px;
  font-size: 14px;
}

.manual-entry summary {
  cursor: pointer;
  color: #3b82f6;
  font-weight: 500;
  padding: 6px 0;
}

.manual-key {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
  line-height: 1.8;
  font-size: 13px;
}

.key-label {
  color: #6b7280;
}

.key-val {
  color: #1f2937;
  font-weight: 600;
}

.secret-key {
  font-family: monospace;
  word-break: break-all;
  letter-spacing: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 15px;
  color: #1f2937;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #3b82f6;
}

.totp-input {
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 10px;
}

.error-msg {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 12px;
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  margin: 0 0 16px;
}

.disable-form {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f3f4f6;
}

.recovery-codes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin: 16px 0;
}

.recovery-code {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: monospace;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  color: #1f2937;
  letter-spacing: 2px;
}

.recovery-actions {
  margin-bottom: 12px;
}

.recovery-warning {
  color: #f59e0b;
  font-size: 13px;
  font-weight: 600;
  margin: 12px 0 20px;
}

.recovery-card {
  border-left: 4px solid #10b981;
}

.action-row-split {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.move-flow {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f3f4f6;
}

.move-verify-toggle {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.toggle-btn {
  flex: 1;
  padding: 8px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn.active {
  border-color: #3b82f6;
  color: #3b82f6;
  background: #eff6ff;
}

.move-notice {
  font-size: 13px;
  color: #6b7280;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
}

.status-info {
  flex: 1;
}

.spinner-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-size: 14px;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.sm {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== DARK MODE ===== */
.dark .totp-page {
  background: transparent;
}

.dark .status-card {
  background: var(--bg-card);
  box-shadow: 0 1px 8px rgba(0,0,0,0.3);
}

.dark .enabled-card {
  border-left-color: #10b981;
}

.dark .page-title,
.dark .step-title,
.dark .status-title {
  color: var(--text-primary);
}

.dark .page-subtitle,
.dark .status-desc,
.dark .step-desc {
  color: var(--text-secondary);
}

.dark .disabled-icon {
  background: #374151;
  color: #9ca3af;
}

.dark .section-label,
.dark .form-label {
  color: var(--text-secondary);
}

.dark .form-input {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.dark .form-input:focus {
  border-color: #3b82f6;
}

.dark .btn-secondary {
  background: #374151;
  color: var(--text-primary);
}

.dark .btn-secondary:hover {
  background: #4b5563;
}

.dark .error-msg {
  background: #450a0a;
  border-color: #7f1d1d;
  color: #f87171;
}

.dark .disable-form,
.dark .move-flow {
  border-top-color: var(--border-primary);
}

.dark .manual-key {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
}

.dark .key-label {
  color: var(--text-secondary);
}

.dark .key-val {
  color: var(--text-primary);
}

.dark .recovery-code {
  background: #374151;
  border-color: var(--border-primary);
  color: var(--text-primary);
}

.dark .toggle-btn {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-secondary);
}

.dark .toggle-btn.active {
  border-color: #3b82f6;
  color: #60a5fa;
  background: #1e3a5f;
}

.dark .move-notice {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  color: var(--text-secondary);
}

.dark .success-title {
  color: #34d399;
}

.dark .no-codes-warning {
  color: #f87171;
}

.dark .qr-img {
  border-color: var(--border-primary);
  background: #fff; /* keep QR on white so it stays scannable */
  border-radius: 12px;
}

.dark .qr-placeholder {
  background: var(--bg-secondary);
}
</style>
